@if (allTasks.length === 0) {
  <div class="empty-state">
    <mat-icon class="empty-icon">work_off</mat-icon>
    <h3>No Tasks</h3>
    <p>No active or completed tasks to display.</p>
    <p>Start processing some files to see task progress here.</p>
  </div>
} @else {
  <div class="tasks-table-wrapper" #scrollContainer (scroll)="onScroll()">
    <table
      mat-table
      [dataSource]="dataSource"
      multiTemplateDataRows
      class="tasks-table"
      [class.scrolled]="isScrolled"
      matSort
      [matSortActive]="'started'"
      matSortDirection="desc"
    >
      <!-- Task Column -->
      <ng-container matColumnDef="task">
        <th mat-header-cell *matHeaderCellDef>Task</th>
        <td mat-cell *matCellDef="let task">
          <div class="task-cell">
            <mat-icon class="task-type-icon">{{ getTaskTypeIcon(task.taskType) }}</mat-icon>
            <div class="task-info">
              <span class="task-name">{{ getTaskTypeLabel(task.taskType) }}</span>
              <span class="task-id">{{ task.taskId | slice: 0 : 8 }}</span>
            </div>
          </div>
        </td>
      </ng-container>

      <!-- Scope Column -->
      <ng-container matColumnDef="scope">
        <th mat-header-cell *matHeaderCellDef>Scope</th>
        <td mat-cell *matCellDef="let task">
          <!-- Scope deep links (Issue #1616). Precomputed by applyTaskScopeLinks() so change
               detection never rebuilds a routerLink array on hover, matching how the Billing
               Transactions "Task Details" column works. -->
          @let scope = task.scopeView;
          <div class="scope-cell">
            @if (scope?.source) {
              <app-file-page-scope mode="link" [source]="scope.source" [pageTitle]="scope.pageTitle" />
            } @else {
              <!-- Unresolvable scope: plain text, no anchor (Issue #1616). -->
              <span class="scope-title">{{ scope?.title || '—' }}</span>
            }
            @if (scope?.subtitle; as subtitle) {
              <span class="scope-subtitle">{{ subtitle }}</span>
            }
          </div>
        </td>
      </ng-container>

      <!-- Status Column with filter -->
      <ng-container matColumnDef="status">
        <th mat-header-cell *matHeaderCellDef>
          <div class="status-header">
            <span>Status</span>
            <button
              mat-icon-button
              [matMenuTriggerFor]="statusFilterMenu"
              class="filter-button"
              [class.active]="statusFilter.length > 0 && statusFilter.length < 5"
            >
              <mat-icon>filter_list</mat-icon>
              @if (statusFilter.length > 0 && statusFilter.length < 5) {
                <span class="filter-badge">{{ statusFilter.length }}</span>
              }
            </button>
            <mat-menu #statusFilterMenu="matMenu" [overlapTrigger]="false">
              <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
              <div (click)="$event.stopPropagation()" class="filter-menu-content">
                <div class="filter-header">
                  <span class="header-title">Filter by Status</span>
                </div>
                <mat-selection-list [formControl]="statusFilterControl" class="status-options">
                  @for (s of allStatuses; track s) {
                    <mat-list-option [value]="s">
                      <span class="status-chip" [class]="'status-' + s">{{ getStatusLabel(s) }}</span>
                    </mat-list-option>
                  }
                </mat-selection-list>
                @if (statusFilter.length === 0) {
                  <p class="filter-hint">When no statuses are selected, all tasks are shown.</p>
                }
                <div class="button-row">
                  <button
                    mat-button
                    color="primary"
                    (click)="clearStatusFilter()"
                    [disabled]="statusFilter.length === 0"
                  >
                    Clear
                  </button>
                </div>
              </div>
            </mat-menu>
          </div>
        </th>
        <td mat-cell *matCellDef="let task">
          <span class="status-chip" [class]="'status-' + task.status">{{ getStatusLabel(task.status) }}</span>
        </td>
      </ng-container>

      <!-- Progress Column -->
      <ng-container matColumnDef="progress">
        <th mat-header-cell *matHeaderCellDef>Progress</th>
        <td mat-cell *matCellDef="let task">
          @if (task.status === 'processing' || task.status === 'pending') {
            <div class="progress-cell">
              <mat-progress-bar
                [mode]="task.progress > 0 ? 'determinate' : 'indeterminate'"
                [value]="task.progress"
                class="mini-progress"
              ></mat-progress-bar>
              @if (task.progress > 0) {
                <span class="progress-text">{{ task.progress }}%</span>
              }
            </div>
          } @else if (task.status === 'complete') {
            <mat-icon class="progress-done-icon">check_circle</mat-icon>
          } @else if (task.status === 'failed') {
            <mat-icon class="progress-failed-icon">error</mat-icon>
          } @else {
            <span class="muted-text">—</span>
          }
        </td>
      </ng-container>

      <!-- Started Column -->
      <ng-container matColumnDef="started">
        <th mat-header-cell *matHeaderCellDef mat-sort-header>Started</th>
        <td mat-cell *matCellDef="let task" [matTooltip]="task.startTime | date: 'medium'">
          {{ formatRelativeTime(task.startTime) }}
        </td>
      </ng-container>

      <!-- Duration Column -->
      <ng-container matColumnDef="duration">
        <th mat-header-cell *matHeaderCellDef>Duration</th>
        <td mat-cell *matCellDef="let task">{{ formatDuration(task) }}</td>
      </ng-container>

      <!-- Cost Column -->
      <ng-container matColumnDef="cost">
        <th mat-header-cell *matHeaderCellDef>Cost</th>
        <td mat-cell *matCellDef="let task" class="cost-cell">
          {{ formatCost(task) }}
        </td>
      </ng-container>

      <!-- Header Row -->
      <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>

      <!-- Data Row (clickable) -->
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        class="task-row"
        [class.expanded-row]="expandedTaskId === row.taskId"
        (click)="toggleExpandedTask(row)"
        tabindex="0"
        role="button"
        [attr.aria-expanded]="expandedTaskId === row.taskId"
        (keydown.enter)="toggleExpandedTask(row)"
        (keydown.space)="$event.preventDefault(); toggleExpandedTask(row)"
      ></tr>

      <!-- Expanded Detail Row -->
      <tr
        mat-row
        *matRowDef="let row; columns: ['expandedDetail']"
        class="detail-row"
        [class.detail-row-expanded]="expandedTaskId === row.taskId"
      ></tr>

      <ng-container matColumnDef="expandedDetail">
        <td mat-cell *matCellDef="let task" [attr.colspan]="displayedColumns.length">
          @if (expandedTaskId === task.taskId) {
            <div class="task-detail-panel" [@detailExpand]>
              <app-unified-task-card
                [task]="getCachedTaskCardData(task)"
                displayMode="card"
                [showProgressDetails]="true"
                [showStepHistory]="true"
                [expandSteps]="true"
                (retryTask)="retryTask($event)"
                (cancelTask)="cancelTask($event)"
              ></app-unified-task-card>
            </div>
          }
        </td>
      </ng-container>
    </table>

    @if (hasMoreTasks) {
      <div class="load-more-row">
        <button mat-stroked-button [disabled]="isLoadingMoreTasks" (click)="loadMoreTasks()">
          <mat-icon>expand_more</mat-icon>
          {{ isLoadingMoreTasks ? 'Loading…' : 'Load more tasks' }}
        </button>
      </div>
    }
  </div>
}
